import Renderable from '../../../Component/Renderable';
import { IGameState } from 'State/Game/IGameState';
import TextComponent from 'Component/Render/Text';
import { Anim, AnimFrom } from 'System/Animation/AnimationHelper';
import { Easing } from '@tweenjs/tween.js';
import { LevelHudLayout } from 'Layouts/LevelHud/LevelHudLayout';
import BaseUISystem from 'System/UI/BaseUISystem';
import TransformComponent from 'Component/Transform';
import { MathHelper } from 'Utility/MathHelper';
import { Layout } from 'Utility/layout/createLayout';

type ScoreBarProps = {
    layout: any;
    system: BaseUISystem;
}

export default class ScoreBar {
    private readonly layout: any;
    private readonly system: BaseUISystem;
    private readonly starPercentAdjustment: number[] = [0.24, 0.59, 1];
    private readonly fullProgressScaleX: number;
    public static instance: ScoreBar;

    constructor(props: ScoreBarProps) {
        ScoreBar.instance = this;
        this.layout = props.layout;
        this.system = props.system;

        this.layout.scoreBarStars.forEach((star:any) => {
            star.getComponent(Renderable).visible = false;
        });

        this.fullProgressScaleX = this.layout.scoreBarProgress.getComponent(TransformComponent).scale.x;
        this.layout.scoreBarProgress.getComponent(TransformComponent).scale.x = 0;
        this.system.scene.on('UpdateScoreProgress', this.handleScoreChange);
        this.layout.scoreDevText.getComponent(Renderable).visible = this.system.store.getState().levelState.isLevelTest;
    }

    public get root(): GameObject {
        return this.layout.scoreBarContainer;
    }

    private readonly handleScoreChange = (score?: number): void => {
        const state: IGameState = this.system.store.getState();
        const preBonusScore: number = state.levelState.bonus
            ? state.levelState.score - state.levelState.bonus
            : state.levelState.score;
        score = score ?? preBonusScore;

        this.layout.scoreDevText.getComponent(TextComponent).text = `${score}`;
        const starRequirements: number[] = state.levelState.stars;

        // Get last star to be earned
        const currentStarIndex = starRequirements
            .reduce((acc, starRequirement, i) => score >= starRequirement ? i : acc, -1);

        const lastEarnedStarPercent = this.starPercentAdjustment[currentStarIndex] ?? 0;
        const nextStarPercent = this.starPercentAdjustment[currentStarIndex + 1] ?? 1;
        const lastEarnedStarScore = starRequirements[currentStarIndex] ?? 0;
        const nextStarScore = starRequirements[currentStarIndex + 1] || starRequirements[currentStarIndex];
        let percent = 1;

        if (nextStarScore !== lastEarnedStarScore) {
            const progressWithinStars = (score - lastEarnedStarScore) / (nextStarScore - lastEarnedStarScore);
            percent = lastEarnedStarPercent + (nextStarPercent - lastEarnedStarPercent) * progressWithinStars;
        }

        percent = MathHelper.clamp(percent, 0, 1);

        this.layout.scoreBarStars.forEach((star:any, index:number) => {
            if (index > currentStarIndex) return;

            const renderable = star.getComponent(Renderable);

            if (!renderable.visible) {
                renderable.visible = true;
                AnimFrom(star, { scaleX: 0, scaleY: 0, duration: 500, easing: Easing.Back.Out });
            }
        });

        const targetScale = this.fullProgressScaleX * percent;
        const duration = (targetScale - this.layout.scoreBarProgress.getComponent(TransformComponent).scale.x) * 4000;
        Anim(this.layout.scoreBarProgress, { scaleX: targetScale, duration });
    };
}
